import { useState } from "react";

// Import lucide icons
import { Info, CheckCircle } from "lucide-react";

// Import inhouse icons
import { AlertCircle, X } from "@/components/icons_new";

// Import utils and helpers
import { useOrderDetailsContext } from "../context/OrderDetailsContext";

// Outline the order combo matrix
const ORDER_ALERT_COMBOS = {
  "pending+processing": {
    icon: <Info className="h-5 w-5" />,
    heading: "Payment is pending",
    message:
      "The payment for this order is still processing. Wait for it to succeed before fulfilling the order.",
    bg: "bg-[#FFFBEB]",
    text: "text-[#92400E]",
    border: "border-[#FACC15]",
  },
  "pending+pending": {
    icon: <Info className="h-5 w-5" />,
    heading: "Awaiting payment",
    message:
      "This order has not been paid yet. Follow up with the customer or resend the invoice.",
    bg: "bg-[#FEF3C7]",
    text: "text-[#92400E]",
    border: "border-[#FCD34D]",
  },
  "failed+processing": {
    icon: <AlertCircle className="h-5 w-5" />,
    heading: "Payment failed",
    message:
      "Do not fulfill this order until the customer completes the payment.",
    bg: "bg-[#FEF2F2]",
    text: "text-[#991B1B]",
    border: "border-[#FECACA]",
  },
  "paid+processing": {
    icon: <Info className="h-5 w-5" />,
    heading: "Ready to fulfill",
    message:
      "Payment is confirmed. Prepare the shipment to move this order forward.",
    bg: "bg-[#E0F2FE]",
    text: "text-[#0369A1]",
    border: "border-[#BAE6FD]",
  },
  "paid+shipped": {
    icon: <CheckCircle className="h-5 w-5" />,
    heading: "Order has shipped",
    message:
      "Tracking is active. Keep an eye on delivery updates and customer feedback.",
    bg: "bg-[#F0FDF4]",
    text: "text-[#166534]",
    border: "border-[#86EFAC]",
  },
  "paid+completed": {
    icon: <CheckCircle className="h-5 w-5" />,
    heading: "Order fulfilled",
    message:
      "This order is marked as completed. No further action is required.",
    bg: "bg-[#F0FDF4]",
    text: "text-[#166534]",
    border: "border-[#86EFAC]",
  },
  "paid+cancelled": {
    icon: <AlertCircle className="h-5 w-5" />,
    heading: "Order cancelled after payment",
    message:
      "Ensure the customer has been refunded and the inventory is updated.",
    bg: "bg-[#FEF2F2]",
    text: "text-[#991B1B]",
    border: "border-[#FECACA]",
  },
};

const normalizeKey = (value) => value?.toLowerCase().trim() ?? "";

export default function OrderStatusAlert() {
  const { order } = useOrderDetailsContext();
  const [isDismissed, setIsDismissed] = useState(false);

  if (!order || isDismissed) return null;

  const comboKey = `${normalizeKey(order.paymentStatus)}+${normalizeKey(order.fulfillmentStatus)}`;
  const alert = ORDER_ALERT_COMBOS[comboKey];

  if (!alert) return null;

  return (
    <div className={`flex items-start justify-between rounded-md border p-4 mb-[1em] text-sm ${alert.bg} ${alert.border}`}>
      <div className="flex items-start gap-3">
        <div className={`mt-1 ${alert.text}`}>{alert.icon}</div>
        <div>
          <p className={`font-semibold ${alert.text}`}>{alert.heading}</p>
          <p className={`mt-1 text-sm ${alert.text}`}>{alert.message}</p>
        </div>
      </div>
      <button
        className={`hover:opacity-70 ${alert.text}`}
        aria-label="Dismiss"
        onClick={() => setIsDismissed(true)}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
}
